import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useScrollLock } from "../hooks/useScrollLock";
import { useAuth } from "../contexts/AuthContext";
import { isNewerThanSeen, latestTimestamp, useNotificationSeenStore } from "../state/useNotificationSeenStore";
import {
    categoryOf,
    useAdminNotifications,
    useIsMediaAdmin,
    NotificationCategory,
    NotificationItem,
    NotificationType,
} from "../hooks/useAdminNotifications";
import { NotificationRow } from "./NotificationRow";
import UserPlusIcon from "./icons/UserPlusIcon";
import TicketIcon from "./icons/TicketIcon";
import UsersGroupIcon from "./icons/UsersGroupIcon";
import ShieldCheckIcon from "./icons/ShieldCheckIcon";
import ClipboardListIcon from "./icons/ClipboardListIcon";
import ShieldExclamationIcon from "./icons/ShieldExclamationIcon";

interface NotificationCenterPanelProps {
    onClose?: () => void;
}

const CATEGORY_ORDER: NotificationCategory[] = ["discipline", "officials", "media"];

const CATEGORY_META: Record<NotificationCategory, { labelKey: string; accent: string }> = {
    discipline: { labelKey: "category_discipline", accent: "bg-rose-400" },
    officials: { labelKey: "category_officials", accent: "bg-sky-400" },
    media: { labelKey: "category_media", accent: "bg-emerald-400" },
};

const GROUP_ORDER: NotificationType[] = [
    "pv_report_check",
    "motif_accumulation",
    "official_request",
    "referee_proposal",
    "official_link",
    "organization",
    "account",
    "league_access",
    "demand",
    "request",
];

const GROUP_META: Record<
    NotificationType,
    { Icon: React.FC<React.SVGProps<SVGSVGElement>>; labelKey: string; tint: string }
> = {
    referee_proposal: {
        Icon: UserPlusIcon,
        labelKey: "group_referee_proposals",
        tint: "text-sky-300",
    },
    organization: {
        Icon: UsersGroupIcon,
        labelKey: "group_organizations",
        tint: "text-orange-300",
    },
    account: {
        Icon: UserPlusIcon,
        labelKey: "group_accounts",
        tint: "text-emerald-300",
    },
    league_access: {
        Icon: ShieldCheckIcon,
        labelKey: "group_league_access",
        tint: "text-amber-300",
    },
    demand: { Icon: TicketIcon, labelKey: "group_demands", tint: "text-violet-300" },
    request: {
        Icon: ClipboardListIcon,
        labelKey: "group_requests",
        tint: "text-sky-300",
    },
    pv_report_check: {
        Icon: ShieldExclamationIcon,
        labelKey: "group_pv_report_check",
        tint: "text-amber-300",
    },
    motif_accumulation: {
        Icon: ShieldExclamationIcon,
        labelKey: "group_motif_accumulation",
        tint: "text-orange-300",
    },
    official_request: {
        Icon: ShieldCheckIcon,
        labelKey: "group_official_request",
        tint: "text-sky-300",
    },
    official_link: {
        Icon: UsersGroupIcon,
        labelKey: "group_official_link",
        tint: "text-sky-300",
    },
};

/**
 * Dropdown panel for the admin notification center. Mirrors JobCenterPanel:
 * createPortal + backdrop + end-aligned glass panel, shared useScrollLock, and
 * z-[10001] so it overlays modals. Reads the exact same react-query data as the
 * bell badge via useAdminNotifications (shared cache — no extra request).
 */
export const NotificationCenterPanel: React.FC<NotificationCenterPanelProps> = ({
    onClose,
}) => {
    const { t } = useTranslation("notifications");
    const { totalPending, items } = useAdminNotifications();
    const isMediaAdmin = useIsMediaAdmin();
    const { user } = useAuth();
    const userId = user?.id;

    // Opening the bell marks the informational items loaded now as seen (the
    // badge drops them), while this opening still shows as new what was new
    // before it. The watermark is their newest added_at (server time), never
    // the client clock; with none loaded, the stored watermark stays.
    const [seenAtOnOpen] = useState(() =>
        userId ? useNotificationSeenStore.getState().officialLinksSeenAt[userId] : undefined
    );
    const [watermarkOnOpen] = useState(() =>
        latestTimestamp(items.filter((i) => i.informational).map((i) => i.createdAt))
    );
    useEffect(() => {
        if (userId && watermarkOnOpen) {
            useNotificationSeenStore.getState().advanceOfficialLinksSeen(userId, watermarkOnOpen);
        }
    }, [userId, watermarkOnOpen]);

    // Panel is only mounted while open, so a constant `true` is correct.
    useScrollLock(true);

    // Close on Escape.
    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose?.();
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [onClose]);

    const grouped = useMemo(() => {
        const buckets = new Map<NotificationType, NotificationItem[]>();
        for (const item of items) {
            const arr = buckets.get(item.type) ?? [];
            arr.push(item);
            buckets.set(item.type, arr);
        }
        const groups = GROUP_ORDER.map((type) => ({
            type,
            items: buckets.get(type) ?? [],
        })).filter((g) => g.items.length > 0);
        return CATEGORY_ORDER.map((category) => {
            const categoryGroups = groups.filter((g) => categoryOf(g.type) === category);
            return {
                category,
                groups: categoryGroups,
                count: categoryGroups.reduce((n, g) => n + g.items.length, 0),
            };
        }).filter((c) => c.count > 0);
    }, [items]);

    const portalTarget =
        typeof document !== "undefined" ? document.body : undefined;
    if (!portalTarget) return null;

    return createPortal(
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-x-0 top-12 sm:top-14 lg:top-16 bottom-0 bg-black/60 backdrop-blur-sm z-[10000]"
                onClick={onClose}
            />

            {/* Panel */}
            <div className="fixed end-2 top-12 sm:top-14 lg:top-16 w-[95vw] sm:w-[400px] max-w-[95vw] max-h-[70vh] flex flex-col bg-[#1e293b] shadow-2xl border border-slate-700 rounded-lg overflow-hidden z-[10001] text-white">
                {/* Header */}
                <div className="flex-shrink-0 px-3 py-2.5 flex items-center justify-between gap-2 bg-[#0f172a] border-b border-slate-700">
                    <h2 className="font-semibold text-sm tracking-wide flex items-center gap-2">
                        <span className="h-2 w-2 rounded-full bg-amber-400" />
                        {t("title")}
                        {totalPending > 0 && (
                            <span className="px-1.5 py-0.5 rounded bg-amber-500/20 text-[10px] font-medium text-amber-200 tabular-nums">
                                {t("total_pending", { count: totalPending })}
                            </span>
                        )}
                    </h2>
                    {isMediaAdmin && (
                        <NavLink
                            to="/media"
                            onClick={onClose}
                            className="text-xs px-2 py-1 rounded border bg-white/10 border-white/10 hover:bg-white/20 transition-colors"
                        >
                            {t("view_all")}
                        </NavLink>
                    )}
                </div>

                {/* Body */}
                <div className="flex-grow overflow-auto bg-[#1e293b]">
                    {items.length === 0 ? (
                        <div className="p-6 text-center text-white/40 text-xs select-none">
                            {t("empty")}
                        </div>
                    ) : (
                        grouped.map(({ category, groups, count }) => (
                            <div key={category}>
                                <div className="flex items-center gap-2 px-3 py-2 bg-[#131c2e] border-b border-slate-700">
                                    <span className={`h-3.5 w-1 rounded-full ${CATEGORY_META[category].accent}`} />
                                    <span className="text-[11px] font-bold uppercase tracking-widest text-white/90">
                                        {t(CATEGORY_META[category].labelKey)}
                                    </span>
                                    <span className="text-white/40 text-[10px] tabular-nums">
                                        ({count})
                                    </span>
                                </div>
                                {groups.map((group) => {
                                    const { Icon, labelKey, tint } = GROUP_META[group.type];
                                    return (
                                        <div key={group.type}>
                                            <div className="sticky top-0 z-[1] flex items-center gap-2 px-3 py-1.5 text-[10px] uppercase tracking-wider bg-[#0f172a]/95 backdrop-blur border-y border-slate-700 text-white/80">
                                                <Icon className={`h-3.5 w-3.5 ${tint}`} />
                                                <span className="font-semibold">
                                                    {t(labelKey)}
                                                </span>
                                                <span className="text-white/50 tabular-nums">
                                                    ({group.items.length})
                                                </span>
                                            </div>
                                            {group.items.map((item) => (
                                                <NotificationRow
                                                    key={item.id}
                                                    item={item}
                                                    onClose={onClose}
                                                    unread={
                                                        !item.informational ||
                                                        isNewerThanSeen(item.createdAt, seenAtOnOpen)
                                                    }
                                                />
                                            ))}
                                        </div>
                                    );
                                })}
                            </div>
                        ))
                    )}
                </div>
            </div>
        </>,
        portalTarget
    );
};

export default NotificationCenterPanel;
